import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import commonService from '../../../services/commonServiceInstance';
import { handleApiCall } from '../../../helpers/handleApiCall';
import { createKeyedThunk } from '../../../redux/utils/createKeyedThunk';

const initialState = {
    patient: {},
    users: [],
    providerTags: null,
    specialties: { options: null, loading: 'idle', error: null },
    documentTypes: null,
    // Task tags cache with loading states
    taskTagsRequests: {},
    taskTagsData: {},
};

/**
 * Loads all specialties with built-in deduplication and caching via createAsyncThunk.
 * - `condition` prevents re-fetching when data is already cached or a request is in-flight.
 * - Lifecycle actions (pending/fulfilled/rejected) manage loading & error state automatically.
 */
export const loadSpecialties = createAsyncThunk(
    'dropdowns/loadSpecialties',
    async (_, { dispatch }) => {
        const result = await handleApiCall(async () => commonService.getSpecialties({}), {
            dispatch,
            errorMessage: 'Failed to load specialties. Please wait a moment, and try again.',
        });
        return result?.specialties || [];
    },
    {
        condition: (_, { getState }) => {
            const { specialties } = getState().dropdowns;
            // Don't fetch if already loaded or currently loading
            if (specialties?.options || specialties?.loading === 'pending') {
                return false;
            }
        },
    },
);

/**
 * TaskTagSelect async thunk - single keyed thunk with condition-based caching
 */
export const getTaskTagsCacheKey = (arg = {}) => {
    const { tagIds, tagNames, patientEncounterId } = arg;
    if (tagIds?.length || tagNames?.length) {
        return `ids:${tagIds?.join(',') || ''}-names:${tagNames?.join(',') || ''}`;
    }
    if (patientEncounterId) return patientEncounterId;
    return 'all-tags';
};

const { thunk: loadTaskTags, addCases: addTaskTagsCases } = createKeyedThunk({
    typePrefix: 'dropdowns/loadTaskTags',
    getCacheKey: getTaskTagsCacheKey,
    call: async (arg, thunkAPI) => {
        const { componentId, patientEncounterId, tagIds, tagNames } = arg || {};
        try {
            return await handleApiCall(
                async () => commonService.getTags({ patientEncounterId, tagIds, tagNames }),
                {
                    dispatch: thunkAPI.dispatch,
                    componentId,
                    errorMessage:
                        'Failed to load available task tags. Please wait a moment, and try again.',
                },
            );
        } catch (error) {
            // Swallow ValidationError so only component-level error UI handles it
            return {
                wasSuccessful: false,
                silent: true,
                errorMessage: error?.message,
                validationErrors: error?.validationErrors,
                statusCode: error?.statusCode,
            };
        }
    },
    selectPayload: (result) => result?.tags || [],
    rejectPayload: () => ({ silent: true, error: null, wasSuccessful: false }),
    selectRequestsFromRoot: (root) => root?.dropdowns?.taskTagsRequests,
    requestsKey: 'taskTagsRequests',
    dataKey: 'taskTagsData',
});

const slice = createSlice({
    name: 'dropdowns',
    initialState,
    reducers: {
        // when options need to be persisted to redux state
        setOptions(state, action) {
            const { key, options } = action.payload;
            if (!state[key]) state[key] = {};
            state[key].options = options;
        },
        setSearchInProgress(state, action) {
            state.searchInProgress = action.payload;
        },
        setDocumentTypes(state, action) {
            state.documentTypes = action.payload;
        },
    },
    extraReducers: (builder) => {
        // Specialties lifecycle
        builder
            .addCase(loadSpecialties.pending, (state) => {
                state.specialties.loading = 'pending';
                state.specialties.error = null;
            })
            .addCase(loadSpecialties.fulfilled, (state, action) => {
                state.specialties.loading = 'fulfilled';
                state.specialties.options = action.payload;
            })
            .addCase(loadSpecialties.rejected, (state, action) => {
                state.specialties.loading = 'rejected';
                state.specialties.error = action.error?.message || 'Failed to load specialties';
            });

        // Task tags single keyed thunk reducers
        addTaskTagsCases(builder);
    },
});

export const { setOptions, setDocumentTypes } = slice.actions;

export const dropdowns = { reducer: slice.reducer, initialState };
export default dropdowns;
export { loadTaskTags };

// Thunks (side effects)

export const searchClientFacilities =
    (searchTerm, clientId, facilityId, requiredPermission) => async (dispatch, getState) => {
        const result = await handleApiCall(
            async () => {
                return await commonService.searchClientFacilities({
                    searchTerm,
                    clientId,
                    facilityId,
                    requiredPermission,
                });
            },
            { dispatch },
        );

        return result?.clientsAndFacilities;
    };

export const searchClients =
    ({ searchTerm, requiredPermission, clientId }) =>
    async (dispatch, getState) => {
        if (!clientId && (searchTerm === null || searchTerm === '')) {
            return [];
        }
        const result = await handleApiCall(
            async () => {
                return await commonService.getAllClients({
                    clientId,
                    requiredPermission,
                    searchTerm,
                });
            },
            { dispatch },
        );

        if (!result?.wasSuccessful) {
            return [];
        }

        return result?.clients;
    };

export const listAllClients = ({ searchTerm }) => {
    return async (dispatch) => {
        const results = await handleApiCall(
            async () => await commonService.listClients({ searchTerm, page: 1, pageSize: 1000 }),
            { dispatch },
        );

        return results.items;
    };
};

export const searchInsurancePlans = (clientId, searchTerm) => async (dispatch) => {
    if (searchTerm === null || searchTerm === '') {
        return [];
    }

    const result = await handleApiCall(
        async () => await commonService.getInsurancePlans({ clientId, searchTerm }),
        { dispatch },
    );

    if (!result.wasSuccessful) {
        return [];
    }

    return result?.insurancePlans;
};

/*
    UserSpecialtiesUserTypesSelect thunks
*/

export const getSpecialtiesAndUserTypes = (data) => {
    return async (dispatch) => {
        const results = await handleApiCall(
            async () => await commonService.getSpecialtiesAndUserTypes(data),
            { dispatch },
        );

        return results?.items;
    };
};

/*
    PatientAsyncSelect thunks
*/

export const findPatients =
    ({ phrase, patientId, onBehalfOfUserId, facilityId, componentId }) =>
    async (dispatch) => {
        if (!phrase?.length && !patientId) return [];

        const errorMessage = `Failed to load patient${
            patientId ? ` :${patientId}` : 's'
        }. Please wait a moment, and try again.`;

        const result = await handleApiCall(
            async () => {
                return await commonService.findPatients(
                    {
                        patientId,
                        phrase,
                        onBehalfOfUserId,
                        facilityId,
                    },
                    true,
                );
            },
            { dispatch, componentId, errorMessage },
        );
        if (!result.wasSuccessful) return false;

        return result.patients.items;
    };

/*
    UserAsyncSelectList thunks
*/
export const searchUsers =
    (
        {
            searchTerm,
            patientId,
            userIds,
            specialtyOrUserType,
            excludedUserIds,
            excludeLoggedInUser = true,
            excludeProviders,
            excludeSuperUsers,
            popularUsersListType,
            patientEncounterId,
            healthcareOrganizationId,
            includeSystemUsers,
            providersOnly,
            clientId,
            sortBy,
            componentId,
        },
        noParallelRequests = true,
    ) =>
    async (dispatch) => {
        const emptyResult = {
            page: 1,
            pageSize: 10,
            items: [],
            totalItemCount: 0,
            totalPageCount: 0,
        };

        if (
            (searchTerm === null || searchTerm === '') &&
            (!userIds || userIds?.length < 1) &&
            !popularUsersListType
        ) {
            return emptyResult;
        }

        const result = await handleApiCall(
            async () => {
                return await commonService.searchUsers(
                    {
                        searchTerm,
                        userIds,
                        excludeLoggedInUser,
                        IncludeSpecialtyName: true,
                        IncludeOrganizationName: true,
                        includeIndicatorOfCareTeamMembershipForPatientId: patientId,
                        specialtyOrUserType,
                        excludeProviders,
                        excludeSuperUsers,
                        onlyPopular:
                            !!patientId || !!patientEncounterId ? popularUsersListType : null,
                        patientId,
                        patientEncounterId,
                        healthcareOrganizationId,
                        includeSystemUsers,
                        excludedUserIds,
                        providersOnly,
                        clientId,
                        sortBy,
                    },
                    noParallelRequests,
                );
            },
            {
                dispatch,
                componentId,
                errorMessage: userIds
                    ? 'Failed to load users. Please wait a moment, and try again.'
                    : 'Failed to load users. Please wait a moment, and try again.',
            },
        );

        if (!result.wasSuccessful) return emptyResult;

        return result?.users;
    };

export const searchProviderUsers =
    (searchTerm, patientId, userIds, healthcareProviderIds) => async (dispatch, getState) => {
        const emptyResult = {
            page: 1,
            pageSize: 10,
            items: [],
            totalItemCount: 0,
            totalPageCount: 0,
        };

        const hasInitialIds =
            (userIds && userIds.length > 0) ||
            (healthcareProviderIds && healthcareProviderIds.length > 0);
        if ((searchTerm === null || searchTerm === '') && !hasInitialIds) {
            return emptyResult;
        }

        const result = await handleApiCall(
            async () => {
                return await commonService.searchProviderUsers({
                    searchTerm,
                    userIds,
                    healthcareProviderIds,
                    includeIndicatorOfCareTeamMembershipForPatientId: patientId,
                });
            },
            { dispatch },
        );

        return result.providers || emptyResult;
    };

/*
    ProxyableUsersDropdown thunks
*/
export const getImpersonatableUsers = () => async (dispatch) => {
    const result = await handleApiCall(
        async () => {
            return await commonService.getImpersonatableUsers({
                includeUnreadMessageCounts: true,
                excludeSelf: true,
            });
        },
        {
            dispatch,
        },
    );

    if (result.wasSuccessful) {
        return result.users;
    }
};

/**
    RecentEncountersAsyncSelect thunks
 */
export const getRecentEncounters =
    (patientId, patientEncounterId, componentId) => async (dispatch) => {
        const result = await handleApiCall(
            async () => {
                return await commonService.getRecentEncounters({
                    patientId,
                    count: 50,
                    includePatientEncounterId: patientEncounterId,
                });
            },
            {
                dispatch,
                componentId,
                errorMessage:
                    'Failed to load recent encounters. Please wait a moment, and try again.',
            },
        );

        if (!result.wasSuccessful) {
            return [];
        }

        return result.patientEncounters || [];
    };

/**
 * SpecialtiesAsyncSelect thunks
 */
export const searchSpecialties =
    (searchTerm, healthcareProviderSpecialtyIds) => async (dispatch) => {
        const result = await handleApiCall(
            async () => {
                return await commonService.getSpecialties({
                    searchTerm,
                    healthcareProviderSpecialtyIds,
                });
            },
            { dispatch },
        );

        if (!result.wasSuccessful) {
            return [];
        }

        return result.specialties;
    };

// loadSpecialties is now a createAsyncThunk defined above the slice (see top of file).

/**
 * ProviderTagSelect thunks
 */
export const loadProviderTags =
    ({ searchTerm, initialTagIds, componentId }) =>
    async (dispatch, getState) => {
        const cachedTags = getState().dropdowns.providerTags;

        if (!cachedTags?.length || searchTerm || initialTagIds?.length) {
            const { providerTags } = await handleApiCall(
                async () => {
                    return await commonService.getProviderTags({
                        searchTerm,
                        HealthcareProviderTagIds: initialTagIds,
                    });
                },
                {
                    dispatch,
                    componentId,
                    errorMessage:
                        'Failed to load available provider tags. Please wait a moment, and try again.',
                },
            );

            dispatch(setOptions({ key: 'providerTags', options: providerTags }));
            return providerTags || [];
        }

        return cachedTags;
    };

/**
 * WorkQueueAsyncSelect thunks
 */
export const getCategorizedWorkQueues =
    ({ componentId }) =>
    async (dispatch) => {
        const result = await handleApiCall(
            async () => {
                return await commonService.getCategorizedWorkQueues();
            },
            {
                dispatch,
                componentId,
                errorMessage: 'Failed to load work queues. Please wait a moment, and try again.',
            },
        );

        const options = result?.items?.flatMap((cat) =>
            cat.workQueues.flatMap((queue) => ({
                label: cat.name + ' > ' + queue.name,
                value: queue?.workQueueId,
                workQueueId: queue?.workQueueId,
                workQueueName: queue?.name,
                workQueueCategoryId: cat?.workQueueCategoryId,
                workQueueCategoryName: cat?.name,
            })),
        );

        dispatch(setOptions({ key: 'categorizedWorkQueues', options: options || [] }));
        return options || [];
    };

export const getDocumentTypes =
    ({ componentId }) =>
    async (dispatch, getState) => {
        const cachedDocumentTypes = getState().dropdowns.documentTypes;

        if (cachedDocumentTypes?.length) return cachedDocumentTypes;

        const result = await handleApiCall(
            async () => await commonService.getPrimaryDocumentTypes(),
            {
                dispatch,
                componentId,
                wasSuccessful: (result) => !!result?.length,
                errorMessage: 'Failed to load document types. Please wait a moment, and try again.',
            },
        );

        if (result?.wasSuccessful === false) {
            return [];
        }

        dispatch(setDocumentTypes(result || []));
        return result || [];
    };
